<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @before-hide="unbindIntegrationReferences"
  >
    <q-card
      class="integration-editor"
    >
      <q-form
        ref="form"
        @submit="onSubmitForm"
      >
        <q-card-section class="row">
          <div
            class="col-12"
          >
            <div class="row full-width items-center q-gutter-y-md q-px-md">
              <div class="col text-h6 ellipsis">
                {{ $t('settings.integrations.importClients.textTitle', {
                  integration: getIntegrationName(connectedClinic.integration)
                }) }}
              </div>
              <div v-if="isPollingDrivenIntegration">
                <q-btn
                  :label="$t('settings.integrations.clients.buttonRefresh')"
                  flat
                  color="primary"
                  :loading="isRefreshing || isLoadingCache"
                  icon="refresh"
                  @click="onRefresh"
                />
              </div>
              <q-input
                v-model="clientFilter"
                borderless
                dense
                clearable
                debounce="300"
                :placeholder="$t('common.buttonSearch')"
              >
                <template #append>
                  <q-icon
                    name="search"
                    color="primary"
                  />
                </template>
              </q-input>
              <div class="col-12 q-mb-md">
                <banner
                  :message="$t(
                    'settings.integrations.importClients.textAvailableLicenses',
                    {availableLicenses: availableLicenses}
                  )"
                />
              </div>
            </div>
            <component
              :is="tableType"
              v-model:selected="selectedRows"
              v-model:pagination="pagination"
              flat
              :bordered="false"
              :rows="isRefreshing ? [] : availableReferences"
              :columns="columns"
              row-key="refId"
              disable-export
              disable-views
              disable-search
              save-filter-id="clientsInvitations"
              :selection="isImporting ? undefined : 'multiple'"
              :disable="isImporting"
              :filter="clientFilter"
              :loading="isRefreshing || isLoadingCache"
              @row-click="onSelectRow"
            />
          </div>
        </q-card-section>
        <q-card-section
          v-if="hasUnits && hasIntegrationUnits"
        >
          <div class="text-overline text-grey-6 text-uppercase">
            {{ $t('settings.integrations.importClients.textMapUnits') }}
          </div>
          <q-markup-table flat>
            <thead>
              <q-th>
                {{ $t(
                  'settings.integrations.importClients.columnIntegrationUnitName',
                  { integration: getIntegrationName(connectedClinic.integration) },
                ) }}
              </q-th>
              <q-th />
              <q-th>
                {{ $t(
                  'settings.integrations.importClients.columnUnitName',
                ) }}
              </q-th>
            </thead>
            <tbody>
              <q-tr
                v-for="integrationUnitName in selectedIntegrationUnitNames"
                :key="integrationUnitName"
              >
                <q-td>
                  {{ integrationUnitName }}
                </q-td>
                <q-td>
                  <q-icon name="arrow_forward" />
                </q-td>
                <q-td>
                  <q-select
                    :model-value="getIntegrationUnitMapping(integrationUnitName)"
                    :options="sortedUnits"
                    outlined
                    dense
                    clearable
                    map-options
                    :disable="isImporting"
                    option-value="id"
                    option-label="unitName"
                    @update:model-value="onSelectUnitMapping(integrationUnitName, $event)"
                  />
                </q-td>
              </q-tr>
            </tbody>
          </q-markup-table>
        </q-card-section>
        <q-card-actions class="row">
          <div class="col">
            <q-linear-progress
              v-if="isImporting"
              :value="importProgress"
              color="positive"
              rounded
            />
          </div>
          <div class="col-auto">
            <q-btn
              v-close-popup
              flat
              :label="$t('common.buttonCancel')"
              :disable="isImporting"
              color="secondary"
              @click="hide"
            />
            <q-btn
              flat
              :label="$t('common.buttonSave')"
              :disable="isImporting"
              type="submit"
              color="primary"
              data-e2e="button-save"
            />
          </div>
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import { filterPropertyTypes } from 'boot/globals';
import Banner from 'components/UI/Banner';
import FilteredTable from 'components/UI/FilteredTable.vue';
import {
  chain, chunk, find, get, isEmpty, isNil, isObject, keys, map, omit,
  reject, size, sortBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { QTable } from 'quasar';
import { BASE_INTEGRATIONS, POLLING_DRIVEN_INTEGRATIONS, V2_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import ClientsDB from 'src/fb/clients-db';
import ClinicClientReferencesDb from 'src/fb/clinicClientReferences-db';
import IntegrationCacheDB from 'src/fb/integrationCache-db';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import LicenseProducts from 'src/mixins/LicenseProducts';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { integrationReferences as useIntegrationReferencesStore } from 'src/store/integrationReferences';
import { network } from 'src/store/network';
import { units } from 'src/store/units';
import { notifyErrorWithDetails } from 'src/utils/AppUtils';
import { sendEventToAloha, updateAppointmentClientReference } from 'src/utils/Integrations';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'ImportClientsDialog',

  components: { FilteredTable, Banner },

  mixins: [Integrations, Interactions, LicenseProducts],

  props: {

    integrationReference: {
      type: [String, Number],
      default: null,
    },

    userId: {
      type: String,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      isRefreshing: false,
      isLoadingCache: false,
      isImporting: false,
      importCount: 0,

      showAllReferences: false,

      selectedRows: [],
      references: [],
      integrationUnitMappings: {},
      groupedRefNames: [],

      clientFilter: null,
      filterAll: null,

      pagination: {
        rowsPerPage: 25,
        sortBy: 'fullName',
        page: 1,
      },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'billing', 'isSupervisor']),
    ...mapState(useIntegrationReferencesStore, ['getReferences', 'getLinkedNames']),
    ...mapState(units, ['units']),
    ...mapState(clients, ['clients']),
    ...mapState(network, ['networkOnline']),

    hasUnits() {
      return !isEmpty(this.units);
    }, // hasUnits()

    hasIntegrationUnits() {
      return !chain(this.selectedIntegrationUnitNames)
        .compact()
        .isEmpty()
        .value();
    },

    sortedUnits() {
      return sortBy(this.units, 'unitName');
    },

    tableType() {
      return this.getIntegrationName(this.connectedClinic.integration) === 'Lumary' ? FilteredTable : QTable;
    },

    selectedIntegrationUnitNames() {
      return chain(this.selectedRows)
        .map(({ integrationUnitName }) => integrationUnitName)
        .uniq()
        .sort()
        .value();
    }, // selectedIntegrationUnitNames()

    availableLicenses() {
      const currentUsage = get(this.billing, ['subscriptionLicenses', 'client', 'currentUsage'], 0);
      const quantity = get(this.billing, ['subscriptionLicenses', 'client', 'quantity'], 0);

      return (quantity - currentUsage);
    }, // availableLicenses()

    importProgress() {
      if (size(this.selectedRows) === 0) {
        return null;
      }
      return this.importCount / size(this.selectedRows);
    },

    columns() {
      const fullName = {
        name: 'fullName',
        field: 'fullName',
        required: true,
        label: this.$t('settings.integrations.clients.columnClientName'),
        align: 'left',
        sortable: true,
        persistent: true,
      };
      const businessUnit = {
        name: 'integrationUnitName',
        field: 'integrationUnitName',
        required: false,
        label: this.$t(
          'settings.integrations.importClients.columnIntegrationUnitName',
          { integration: this.getIntegrationName(this.connectedClinic.integration) },
        ),
        align: 'left',
        sortable: false,
        persistent: true,
        filterable: true,
        filterType: filterPropertyTypes.StringMatch,
      };

      if (this.connectedClinic.integration === 'aloha') {
        return [fullName];
      }

      return [fullName, businessUnit];
    },

    availableReferences() {
      const linkedReferences = keys(this.groupedRefNames);

      return this.references.filter(
        // exclude clients already inactive on the integration side (HR-4833)
        ({ refId, active }) => active !== false && this.isReferenceAvailableOrUnlinked(linkedReferences, refId),
      );
    },

    cantBindTooltipText() {
      return this.userId
        ? this.$t('users.modals.editUser.userAlreadyHasBoundReference')
        : this.$t('clients.modals.editClient.clientAlreadyHasBoundReference');
    },

    /**
     * These integrations own their references through a cron and the Refresh button, so the list
     * can be read once and cached. The event-driven ones stay on a live binding.
     */
    isPollingDrivenIntegration() {
      return POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration);
    },
  },

  watch: {
    // `references` refreshes (cache reload or live binding) can drop a row that was already
    // selected, e.g. a client discharged on the integration side mid-selection — QTable doesn't
    // prune `selected` on its own, so a stale row would otherwise still reach importClients() (HR-4833)
    references() {
      this.selectedRows = this.selectedRows.filter(
        ({ refId }) => this.availableReferences.some((ref) => ref.refId === refId),
      );
    },
  },

  async created() {
    await this.loadIntegrationReferences();
  },

  methods: {
    ...mapActions(useIntegrationReferencesStore, ['loadReferences']),

    /**
     * Both halves of the list are useless on their own, so a failure in either has to surface.
     * Without this the table just renders empty, which reads as "there is nothing to import".
     */
    async loadIntegrationReferences({ force = false } = {}) {
      try {
        if (this.isPollingDrivenIntegration) {
          await this.loadCachedReferences(force);
          return;
        }

        await this.getReferencesLinks();
        await this.bindIntegrationReferences();
      } catch (err) {
        this.notifyNegative(
          this.$t('common.errors.databaseReadError'),
          {},
          { copyActionValue: err.details?.fullMessage || err.message },
        );
      }
    }, // loadIntegrationReferences()

    async loadCachedReferences(force = false) {
      this.isLoadingCache = true;

      try {
        await this.loadReferences('client', { force });

        this.references = this.getReferences('client');
        this.groupedRefNames = this.getLinkedNames('client');
      } finally {
        this.isLoadingCache = false;
      }
    }, // loadCachedReferences()

    show() {
      this.$refs.dialog.show();
    }, // show()

    async hide() {
      await this.unbindIntegrationReferences();
      this.$emit('hide');

      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    onSelectUnitMapping(integrationUnitName, unit) {
      this.integrationUnitMappings[integrationUnitName] = unit;
    }, // onSelectUnit()

    onSelectRow(evt, row) {
      if (!this.isRefAvailable(row)) {
        return;
      }
      if (chain(this.selectedRows).map('refId').includes(get(row, 'refId')).value()) {
        this.selectedRows = reject(this.selectedRows, { refId: row.refId });
      } else {
        this.selectedRows.push(row);
      }
    }, // onSelectRow()

    isRefAvailable(reference) {
      return this.integrationReference === reference.refId
        || (find(this.availableReferences, (availableRef) => (reference.refId === availableRef.refId))
          && isNil(this.integrationReference));
    },

    async getReferencesLinks() {
      this.isLoadingCache = true;

      try {
        const { data } = await callFirebaseFunction('getReferencesLinks', { referenceType: 'client' });
        this.groupedRefNames = data;
      } finally {
        this.isLoadingCache = false;
      }
    },

    onSubmitForm() {
      if (isEmpty(this.selectedRows)) {
        return;
      }
      if (size(this.selectedRows) > this.availableLicenses) {
        this.notifyNegative(this.$t('settings.integrations.importClients.errorNotEnoughLicenses'));
        return;
      }
      if (this.hasUnits
        && this.hasIntegrationUnits
        && chain(this.selectedIntegrationUnitNames)
          .some((integrationUnitName) => !this.integrationUnitMappings[integrationUnitName])
          .value()
      ) {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('settings.integrations.importClients.confirmBusinessUnitsNotMapped'),
          () => {
            this.importClients();
          },
        );
        return;
      }
      this.importClients();
    }, // onSubmitForm()

    async onRefresh() {
      this.isRefreshing = true;
      try {
        await callFirebaseFunction('refreshIntegrationRefs', {
          clinicId: this.userDetails.clinic,
          objectType: 'client',
        }, { timeout: 8 * 60 * 1000 });

        if (this.isPollingDrivenIntegration) {
          await this.loadCachedReferences(true);
          return;
        }

        await this.unbindIntegrationReferences();
        await this.getReferencesLinks();
        await this.bindIntegrationReferences();
      } catch (err) {
        notifyErrorWithDetails(err.message, err.details?.fullMessage);
      } finally {
        this.isRefreshing = false;
      }
    }, // onRefresh()

    async bindIntegrationReferences() {
      this.isLoadingCache = true;
      if (!BASE_INTEGRATIONS.includes(this.connectedClinic.integration)) {
        const clinicClientReferencesDb = new ClinicClientReferencesDb(
          this.userDetails.clinic,
          this.connectedClinic.integration,
        );

        try {
          await clinicClientReferencesDb.bindAll(this, 'references');
        } catch (err) {
          console.error(err);
          logEvent(err, {
            context: {
              context_data: {
                integration: this.connectedClinic.integration,
              },
            },
          });
        } finally {
          this.isLoadingCache = false;
        }

        return;
      }

      const integrationCacheDb = new IntegrationCacheDB();
      try {
        await integrationCacheDb.bindAll(
          this,
          'references',
          [
            ['clinic', '==', this.userDetails.clinic],
            ['type', '==', 'client'],
          ],
        );
      } finally {
        this.isLoadingCache = false;
      }
    }, // bindIntegrationReferences()

    unbindIntegrationReferences() {
      if (this.isPollingDrivenIntegration) {
        return null;
      }

      const integrationCacheDb = new IntegrationCacheDB();
      return integrationCacheDb.unbind(this, 'references');
    },

    async importClients() {
      this.isImporting = true;
      this.importCount = 0;
      try {
        const clientsDb = new ClientsDB();
        const createClientActions = chain(this.selectedRows)
          .map(async (row) => {
            const unit = get(this.integrationUnitMappings, [row.integrationUnitName, 'id'], null);
            const client = {
              country: this.billing.country || null,
              ...omit(row, ['id', 'refId', 'fullName', 'integrationUnitName', 'alias']),
              clientName: row.fullName,
              integrationReference: row.refId,
              alias: row.alias || '',
              units: unit ? [unit] : [],
              type: 'client',
              status: 'active',
            };

            // backwards compatability for synced Aloha clients with invalid address
            if (this.connectedClinic.integration === 'aloha' && isObject(row.address1)) {
              const { Street1, Street2, City, ZipPostal, StateProvince } = row.address1;

              client.address1 = Street1;
              client.address2 = Street2;
              client.city = City;
              client.postalCode = ZipPostal;
              client.state = StateProvince;
            }

            const newClient = await clientsDb.create(client);
            this.importCount += 1;

            if (this.connectedClinic.integration === 'aloha' && row.refId && newClient.id) {
              await updateAppointmentClientReference(this.connectedClinic.id, newClient.id, row.refId);
              await sendEventToAloha(this.connectedClinic.id, 'PatientMappingsAdded', { refId: row.refId });
            }

            if (
              V2_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration)
              && row.refId
              && newClient.id
            ) {
              callFirebaseFunction('backfillV2AppointmentsForRef', {
                clinicId: this.connectedClinic.id,
                refId: row.refId,
                objectType: 'client',
              }).catch((err) => console.error('backfillV2AppointmentsForRef failed', err));
            }
          })
          .value();
        await Promise.all(createClientActions);
        await this.updateLicenseUsage();

        this.notifySuccess(this.$t('settings.integrations.importClients.notifySuccess', { count: this.importCount }));

        if (POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration)) {
          this.refreshIntegrationAppointments(map(this.selectedRows, 'refId'));
        }
      } finally {
        this.isImporting = false;
      }
      this.$emit('ok');
      this.hide();
    }, // importClients()

    async refreshIntegrationAppointments(refIds) {
      const totalRefs = size(refIds);
      let countRefs = 0;
      let dialogHidden = false;
      const dialog = this.$q.dialog({
        message: this.$t('settings.integrations.importClients.refreshingText', { countRefs, totalRefs }),
        progress: true,
        persistent: false,
        ok: false,
      }).onDismiss(() => {
        dialogHidden = true;
      });

      // we simulate some progress here...
      const chunkSize = 5;

      // Refresh appointments for the imported clients but be nice and chunk the requests
      const clientChunks = chunk(refIds, chunkSize);
      // eslint-disable-next-line no-restricted-syntax
      for (const clientChunk of clientChunks) {
        const refreshActions = map(
          clientChunk,
          (objectId) => (callFirebaseFunction('refreshAppointments', {
            clinicId: this.userDetails.clinic,
            objectType: 'client',
            objectId,
            ignoreModifiedSince: true,
          }, {
            timeout: 540000, // timeout aligned with firebase function timeout
          })),
        );
        try {
          // eslint-disable-next-line no-await-in-loop
          await Promise.all(refreshActions);
        } catch (err) {
          const isOfflineError = ['internal', 'functions/internal'].includes(err.code)
            && err.message === 'internal'
            && !this.networkOnline;
          const errorMessage = isOfflineError ? this.$t('common.noInternetConnectionMessage') : err.message;
          notifyErrorWithDetails(errorMessage, err.details?.fullMessage);
        } finally {
          countRefs += size(clientChunk);
          if (dialog) {
            dialog.update({
              message: this.$t('settings.integrations.importClients.refreshingText', { countRefs, totalRefs }),
            });
          }
        }
      }
      if (!dialogHidden) {
        dialog.hide();
      }
      this.notifySuccess(this.$t('settings.integrations.importClients.refreshedText', { totalRefs }));
    },

    getIntegrationUnitMapping(integrationUnitName) {
      return get(this.integrationUnitMappings, integrationUnitName, null);
    }, // getIntegrationUnitMapping()

    isReferenceAvailableOrUnlinked(linkedReferences, refId) {
      if (refId === null || refId === undefined) {
        return false;
      }

      const refIdString = refId.toString();
      return this.integrationReference?.toString() === refIdString || !linkedReferences.includes(refIdString);
    }, // isReferenceAvailable()

  },
};
</script>
<style lang="scss" scoped>
.integration-editor {
  min-width:70vw;
}
</style>
